import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Newspaper, Loader2, Settings, Tag, Building2 } from "lucide-react";
import { useNewsPreferences, NewsPreferences } from "@/hooks/useNewsPreferences";
import { GeneralTab } from "./news/GeneralTab";
import { TopicsTab } from "./news/TopicsTab";
import { CompaniesTab } from "./news/CompaniesTab";
import { formatDistanceToNow } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";

export const NewsPreferencesSettings = () => {
  const { preferences, isLoading, updatePreferences, isUpdating } = useNewsPreferences();
  const [localPreferences, setLocalPreferences] = useState<Partial<NewsPreferences>>({});
  const [keywordsInput, setKeywordsInput] = useState<string>('');
  const [additionalCompaniesInput, setAdditionalCompaniesInput] = useState<string>('');

  useEffect(() => {
    if (preferences?.tracked_industry_keywords) {
      setKeywordsInput(preferences.tracked_industry_keywords.join(', '));
    }
    if (preferences?.additional_companies) {
      setAdditionalCompaniesInput(preferences.additional_companies.join(', '));
    }
  }, [preferences]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            <CardTitle>News Feed Settings</CardTitle>
          </div>
          <CardDescription>
            Customize your personalized news feed preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!preferences) return null;

  const currentPreferences = { ...preferences, ...localPreferences };
  const hasChanges = 
    Object.keys(localPreferences).length > 0 || 
    (keywordsInput !== '' && keywordsInput !== (preferences.tracked_industry_keywords?.join(', ') || '')) ||
    (additionalCompaniesInput !== '' && additionalCompaniesInput !== (preferences.additional_companies?.join(', ') || ''));

  const handleChange = (updates: Partial<NewsPreferences>) => {
    setLocalPreferences((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = () => {
    const keywords = keywordsInput
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0)
      .slice(0, 20);
    
    const companies = additionalCompaniesInput
      .split(',')
      .map(c => c.trim())
      .filter(c => c.length > 0)
      .slice(0, 20);
    
    updatePreferences({
      ...localPreferences,
      tracked_industry_keywords: keywords,
      additional_companies: companies
    });
    setLocalPreferences({});
    setKeywordsInput('');
    setAdditionalCompaniesInput('');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Newspaper className="h-5 w-5" />
              <CardTitle>News Feed Settings</CardTitle>
            </div>
            <CardDescription className="mt-2">
              Customize your personalized news feed preferences
            </CardDescription>
            {preferences.last_fetch_at && (
              <p className="text-xs text-muted-foreground mt-1">
                Last updated: {formatDistanceToNow(new Date(preferences.last_fetch_at), { addSuffix: true })}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1 bg-muted/50">
            <TabsTrigger value="general" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
              <Settings className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="topics" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
              <Tag className="h-4 w-4" />
              Topics
            </TabsTrigger>
            <TabsTrigger value="companies" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
              <Building2 className="h-4 w-4" />
              Companies
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6">
            <GeneralTab preferences={currentPreferences} onChange={handleChange} />
          </TabsContent>

          <TabsContent value="topics" className="mt-6">
            <TopicsTab 
              preferences={currentPreferences} 
              onChange={handleChange}
              keywordsInput={keywordsInput || preferences.tracked_industry_keywords?.join(', ') || ''}
              onKeywordsInputChange={setKeywordsInput}
            />
          </TabsContent>

          <TabsContent value="companies" className="mt-6">
            <CompaniesTab 
              preferences={currentPreferences} 
              onChange={handleChange}
              additionalCompaniesInput={additionalCompaniesInput}
              onAdditionalCompaniesInputChange={setAdditionalCompaniesInput}
            />
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-between mt-6 pt-6 border-t">
          <div className="text-sm text-muted-foreground">
            {hasChanges ? (
              <span className="text-primary font-medium">You have unsaved changes</span>
            ) : (
              <span>Tracking {currentPreferences.enabled_categories?.length || 0} topics, {(currentPreferences.additional_companies?.length || 0) + (currentPreferences.track_contact_companies ? 1 : 0)} company sources</span>
            )}
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isUpdating}
          >
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
